/**
 * 頁 3／5 — 入庫列表
 *   · 供應商 → **只睇自己**嘅發貨單（後端 `supplierScope` 把關，前端唔傳 supplierId）
 *   · 物流／職員／管理員 → 睇全部（物流通常篩「已申報」＝ 待收貨）
 */
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import EmptyState from '@coeldery/empty-state';
import FormSelectField from '@coeldery/form-select-field';
import { btnPrimary, card, hint as hintStyle } from '@coeldery/ui-kit';
import IntakeStatusChip, { INTAKE_STATUSES, statusLabelKey } from '../components/IntakeStatusChip';
import { ApiError, api, type IntakeBatch, type IntakeStatus, type Me } from '../lib/api';

export default function Intakes({ user }: { user: Me }) {
  const { t } = useTranslation();
  const [status, setStatus] = useState<IntakeStatus | ''>('');
  const [batches, setBatches] = useState<IntakeBatch[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const res = await api.intakes(status);
      setBatches(res.batches ?? []);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [status, t]);

  useEffect(() => {
    void load();
  }, [load]);

  const isSupplier = user.role === 'supplier';
  const canCreate = user.role !== 'logistics';
  const title = isSupplier ? t('intake.titleSupplier') : t('intake.titleLogistics');

  const statusOptions = [
    { value: '', label: t('intake.allStatus') },
    ...INTAKE_STATUSES.map((s) => ({ value: s as string, label: t(statusLabelKey(s)) })),
  ];

  return (
    <>
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '10px',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 800 }}>{title}</h2>
        {canCreate && (
          <Link
            to="/intakes/new"
            style={{ ...btnPrimary, display: 'inline-flex', alignItems: 'center', textDecoration: 'none' }}
          >
            {t('intake.newBatch')}
          </Link>
        )}
      </div>

      <div style={{ margin: '12px 0' }}>
        <FormSelectField
          id="f-intake-status"
          labelKey="intake.filterStatus"
          value={status}
          onChange={(v) => setStatus(v as IntakeStatus | '')}
          options={statusOptions}
        />
      </div>

      {loading && <p style={hintStyle}>{t('common.loading')}</p>}
      {error && (
        <p style={{ ...hintStyle, color: 'var(--color-accent)', fontWeight: 700 }} role="alert">
          {error}{' '}
          <button
            type="button"
            onClick={() => void load()}
            style={{ border: 'none', background: 'none', color: 'inherit', textDecoration: 'underline', cursor: 'pointer' }}
          >
            {t('common.refresh')}
          </button>
        </p>
      )}

      {!loading && batches.length === 0 && (
        <EmptyState
          titleKey={status === 'declared' ? 'intake.emptyPendingTitle' : 'intake.emptyTitle'}
          bodyKey={status === 'declared' ? 'intake.emptyPendingBody' : 'intake.emptyBody'}
        />
      )}

      {batches.length > 0 && (
        <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
          {batches.map((b) => (
            <li key={b.id} style={{ ...card, padding: 0 }}>
              <Link
                to={`/intakes/${b.id}`}
                style={{
                  display: 'block',
                  padding: '14px 16px',
                  textDecoration: 'none',
                  color: 'inherit',
                }}
              >
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
                  <strong>{b.batchNo}</strong>
                  <IntakeStatusChip status={b.status} />
                </div>
                <p style={hintStyle}>
                  {b.declaredShipDate ? `${t('intake.declaredShipDate')}：${b.declaredShipDate}` : ''}
                  {b.receivedDate ? ` · ${t('intake.receivedDate')}：${b.receivedDate}` : ''}
                </p>
                <p style={hintStyle}>
                  {t('intake.declaredCbm')}：{b.declaredCbm}
                  {b.receivedCbm > 0 ? ` · ${t('intake.receivedCbm')}：${b.receivedCbm}` : ''}
                  {b.handlingFee > 0 ? ` · ${t('intake.handlingFee')}：HK$${b.handlingFee}` : ''}
                </p>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </>
  );
}
